import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Timer } from 'lucide-react';

type SessionTimerProps = {
  /** Seconds left right now. A new value re-syncs the countdown (for example from the server). */
  initialSeconds?: number;
  /** The full length, used only to size the progress bar. */
  totalSeconds?: number;
  onExpire: () => void;
  inline?: boolean;
};

/** Highlight / urgent state starts at 30 seconds remaining. */
const URGENT_SECONDS = 30;

export function SessionTimer({
  initialSeconds = 600,
  totalSeconds,
  onExpire,
  inline = false,
}: SessionTimerProps) {
  const [left, setLeft] = useState(initialSeconds);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    expiredRef.current = false;
    setLeft(initialSeconds);
  }, [initialSeconds]);

  useEffect(() => {
    if (left > 0) {
      const t = window.setTimeout(() => setLeft((s) => s - 1), 1000);
      return () => window.clearTimeout(t);
    }
    if (!expiredRef.current) {
      expiredRef.current = true;
      onExpireRef.current();
    }
    return undefined;
  }, [left]);

  if (typeof document === 'undefined') return null;

  const mm = String(Math.floor(left / 60)).padStart(2, '0');
  const ss = String(left % 60).padStart(2, '0');
  const urgent = left <= URGENT_SECONDS;
  const pct = Math.max(0, Math.min(100, (left / (totalSeconds ?? initialSeconds)) * 100));

  if (inline) {
    return (
      <div
        className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold shadow-2xs border transition-colors ${
          urgent
            ? 'border-red-300 bg-red-50 text-red-700 animate-pulse'
            : 'border-amber-200/90 bg-amber-50/95 text-amber-900'
        }`}
        role="status"
        aria-live={urgent ? 'assertive' : 'polite'}
        title="Your selected time slots are reserved. Complete booking before timer ends."
      >
        <Timer className="h-3.5 w-3.5 shrink-0 text-amber-600" />
        <span className="text-[11px] font-medium hidden sm:inline">
          {urgent ? 'Expiring:' : 'Slots reserved:'}
        </span>
        <span className="font-mono text-[11px] font-bold tabular-nums">
          {mm}:{ss}
        </span>
      </div>
    );
  }

  return createPortal(
    <div
      className={`camp-session-timer fixed left-4 sm:left-6 bottom-20 z-40 w-[min(calc(100vw-2rem),14rem)] overflow-hidden rounded-xl border shadow-lg ${
        urgent
          ? 'camp-session-timer--urgent border-red-300 bg-red-50 text-red-900'
          : 'border-amber-200 bg-amber-50/95 text-amber-950 backdrop-blur-sm'
      }`}
      role="status"
      aria-live={urgent ? 'assertive' : 'polite'}
      title={
        urgent
          ? 'Less than 30 seconds left. Finish booking now, or these time slots will be released.'
          : 'Your selected time slots are reserved. Complete booking before the timer ends — campaign details stay saved.'
      }
    >
      <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
        <div
          className={`flex min-w-0 items-center gap-1.5 font-semibold ${
            urgent ? 'text-red-700' : 'text-amber-900'
          }`}
        >
          <Timer
            className={`h-3.5 w-3.5 shrink-0 ${urgent ? 'animate-pulse' : ''}`}
          />
          <span className="truncate text-[11px] leading-tight">
            {urgent ? 'Complete soon' : 'Slots reserved'}
          </span>
        </div>
        <span
          className={`shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[12px] font-bold tabular-nums ${
            urgent
              ? 'animate-pulse bg-red-600 text-white'
              : 'bg-amber-100 text-amber-950'
          }`}
        >
          {mm}:{ss}
        </span>
      </div>
      <div className="h-0.5 w-full bg-black/5">
        <div
          className={`h-full transition-[width] duration-1000 ease-linear ${
            urgent ? 'bg-red-500' : 'bg-amber-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>,
    document.body,
  );
}
